<template>
  <v-app :class="routeClasses">
    <v-locale-provider>
      <h-theme-pack-provider>
        <component :is="hLayout">
          <router-view :key="$route.fullPath" class="app-container" />
        </component>
        <h-alerts-provider location="bottom-center" />
        <h-confirmation-dialog
          :model-value="confirmState.isVisible.value"
          :title="confirmState.title.value"
          :message="confirmState.message.value"
          :confirm-text="confirmState.confirmText.value"
          :cancel-text="confirmState.cancelText.value"
          :max-width="confirmState.maxWidth.value"
          @update:model-value="(val) => (confirmState.isVisible.value = val)"
          @confirm="confirmState.handleConfirm"
          @cancel="confirmState.handleCancel"
        />
        <profile-modal v-if="isAppReady" />
      </h-theme-pack-provider>
    </v-locale-provider>
  </v-app>
</template>

<script setup>
import { useRouteQuery } from '@vueuse/router';
import { HHelixLayout } from '@onify/helix-pages';
import {
  useHConfirmState,
  useBrandingOverrides,
  useCustomStyles,
  shouldAlertBootConfigDefaults,
  useHTranslate,
  useAdministrationRedirect,
} from '@onify/helix-core';
import ProfileModal from './onify/citizen/components/profile-modal.vue';

const confirmState = useHConfirmState();

const onifyAuthTokenRouteQuery = useRouteQuery('onify_token');

const route = useRoute();

const routeClasses = computed(() =>
  route.path
    .split('/')
    .filter((routeClass) => !!routeClass)
    .map((routeClass) => `h--${routeClass}`),
);

const { hAddAlert } = useHAlerts();

const { hLayout } = useHRouteLayout(HHelixLayout);

useMaintenanceRedirect();
useAdministrationRedirect();
useSettingsAutoRefresh();
useBrandingOverrides();
useCustomStyles();

const isAppReady = ref(false);

onMounted(() => {
  if (onifyAuthTokenRouteQuery.value) {
    onifyAuthTokenRouteQuery.value = null;
  }

  isAppReady.value = true;

  // One non-blocking alert when public bootstrap could not reach the API.
  // Empty API payloads are valid — only an unreachable API triggers this.
  if (shouldAlertBootConfigDefaults()) {
    hAddAlert({
      id: 'boot-config-bundled-defaults',
      type: 'warning',
      title: useHTranslate('app_boot_config_defaults_title'),
      body: useHTranslate('app_boot_config_defaults_body'),
      closable: true,
      timeout: 12_000,
    });
  }
});

onErrorCaptured((error) => {
  // Display Error
  hAddAlert(hCreateGenericErrorAlert(error));
});
</script>

<style lang="scss">
.v-locale-provider {
  height: 100%;
}
</style>
